import { useEffect, useId, useRef, useState } from 'react';
import { platformRegistrationSettingsApi } from '../api.js';
import {
  canManageRegistrationSettings,
  checkedRegistrationSettings,
  checkedRegistrationSettingsSave,
  registrationReadinessMessage,
  registrationDraftBlockedReason,
  canSaveRegistrationSettings,
} from '../platform-registration-settings.js';

export function PlatformRegistrationSettingsView({ snapshot, draft = false, captchaDraft = snapshot?.captchaRequired ?? false, phase = 'loading', notice = '', onChange, onCaptchaChange, onSave, onReload, feedbackRef }) {
  const id = useId();
  const busy = phase === 'loading' || phase === 'saving';
  const confirmed = phase === 'ready';
  const blockedReason = registrationDraftBlockedReason(snapshot, captchaDraft);
  const canSave = confirmed && canSaveRegistrationSettings(snapshot, draft, captchaDraft);
  const status = busy ? phase === 'saving' ? 'Saving…' : 'Checking…'
    : !confirmed ? phase === 'unknown' ? 'Unconfirmed' : 'Unavailable'
      : snapshot.effectiveEnabled ? 'On' : snapshot.enabled ? 'Not active' : 'Off';
  return <section className="management-registration" aria-labelledby={`${id}-title`}>
    <div className="management-registration-heading">
      <h2 id={`${id}-title`}>Self-service registration</h2>
      <span className="management-status" data-status={confirmed && snapshot.effectiveEnabled ? 'active' : confirmed && snapshot.enabled ? 'suspended' : undefined}>{status}</span>
    </div>
    <p id={`${id}-description`}>{['staging_public', 'production_public'].includes(snapshot?.scope) ? 'Anyone can create an account and company when registration is enabled.' : 'Only approved users can create an account and company.'} Existing sign-in and invitations are unaffected.</p>
    {snapshot && <form className="management-registration-form" onSubmit={event => { event.preventDefault(); if (canSave) onSave(); }} aria-busy={busy}>
      <div>
        <div className="management-registration-choice">
          <label htmlFor={`${id}-enabled`}>
            <input id={`${id}-enabled`} type="checkbox" checked={draft}
              disabled={!confirmed || (blockedReason !== null && !draft)}
              aria-describedby={`${id}-description ${id}-readiness`}
              onChange={event => onChange(event.target.checked)} />
            Enable self-service registration
          </label>
          <small>{confirmed ? 'Saved setting' : 'Last confirmed setting'}: {snapshot.enabled ? 'On' : 'Off'}</small>
        </div>
        <div className="management-registration-choice">
          <label htmlFor={`${id}-captcha`}>
            <input id={`${id}-captcha`} type="checkbox" checked={captchaDraft}
              disabled={!confirmed || snapshot.blockedReason === 'unsupported_environment'}
              aria-describedby={`${id}-captcha-description ${id}-readiness`}
              onChange={event => onCaptchaChange(event.target.checked)} />
            Require CAPTCHA for new accounts
          </label>
          <small>{confirmed ? 'Saved setting' : 'Last confirmed setting'}: {snapshot.captchaRequired ? 'Required' : 'Not required'}</small>
          <p id={`${id}-captcha-description`}>{captchaDraft
            ? 'New accounts must complete a CAPTCHA check.'
            : 'Without CAPTCHA, automated signups may increase.'}</p>
        </div>
      </div>
      <button type="submit" className="management-primary" disabled={!canSave}>Save</button>
    </form>}
    <p id={`${id}-readiness`} className="management-registration-readiness">{snapshot && registrationReadinessMessage(blockedReason)}</p>
    <div className="management-registration-feedback">
      <p ref={feedbackRef} tabIndex={-1} role={['unknown', 'error', 'forbidden'].includes(phase) ? 'alert' : 'status'}>
        {notice || (phase === 'loading' ? 'Loading registration settings…' : '')}
      </p>
      <button type="button" disabled={busy} onClick={onReload}>Reload settings</button>
    </div>
  </section>;
}

function RegistrationSettingsController() {
  const [snapshot, setSnapshot] = useState(null), [draft, setDraft] = useState(false);
  const [captchaDraft, setCaptchaDraft] = useState(false);
  const [phase, setPhase] = useState('loading'), [notice, setNotice] = useState('');
  const active = useRef(null), sequence = useRef(0), pending = useRef(false);
  const feedback = useRef(null), focusFeedback = useRef(false);

  function begin() {
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    const requestId = ++sequence.current;
    const timeout = setTimeout(() => controller.abort(), 15_000);
    pending.current = true;
    return { signal: controller.signal, current: () => sequence.current === requestId,
      finish: () => { clearTimeout(timeout); if (sequence.current === requestId) { pending.current = false; active.current = null; } } };
  }

  async function load(successNotice = '') {
    const request = begin(); setPhase('loading'); setNotice('');
    try {
      const result = checkedRegistrationSettings(await platformRegistrationSettingsApi.get({ signal: request.signal }));
      if (!request.current()) return;
      setSnapshot(result); setDraft(result.enabled); setCaptchaDraft(result.captchaRequired); setPhase('ready'); setNotice(successNotice);
    } catch (error) {
      if (!request.current()) return;
      setPhase(error?.status === 403 ? 'forbidden' : 'error');
      setNotice(error?.status === 403 ? 'You do not have access to registration settings.' : 'Registration settings could not be loaded. Reload before making a change.');
    } finally { request.finish(); }
  }

  async function save() {
    if (pending.current || phase !== 'ready' || !canSaveRegistrationSettings(snapshot, draft, captchaDraft)) return;
    const body = { enabled: draft, captchaRequired: captchaDraft, expectedRevision: snapshot.revision };
    const request = begin(); setPhase('saving'); setNotice(''); focusFeedback.current = true;
    try {
      const result = checkedRegistrationSettingsSave(await platformRegistrationSettingsApi.update(body, { signal: request.signal }), body);
      if (!request.current()) return;
      setSnapshot(result); setDraft(result.enabled); setCaptchaDraft(result.captchaRequired); setPhase('ready'); setNotice('Registration settings saved.');
    } catch (error) {
      if (!request.current()) return;
      if (error?.status === 409) {
        await load(error.code === 'registration_settings_not_ready'
          ? 'Registration readiness changed. Latest settings loaded. Review before saving.'
          : 'Settings changed. Latest settings loaded. Review before saving.');
      } else if (error?.status === 403 || error?.status === 400) {
        setPhase(error.status === 403 ? 'forbidden' : 'error');
        setNotice(error.status === 403 ? 'You no longer have permission to change registration settings.' : 'This change was not accepted. Reload settings before trying again.');
      } else {
        setPhase('unknown'); setNotice('Save could not be confirmed. Reload settings before making another change.');
      }
    } finally { request.finish(); }
  }

  useEffect(() => { load(); return () => { sequence.current++; active.current?.abort(); pending.current = false; }; }, []);
  useEffect(() => {
    if (focusFeedback.current && notice && !['loading', 'saving'].includes(phase)) {
      feedback.current?.focus(); focusFeedback.current = false;
    }
  }, [phase, notice]);

  return <PlatformRegistrationSettingsView snapshot={snapshot} draft={draft} captchaDraft={captchaDraft} phase={phase} notice={notice} feedbackRef={feedback}
    onChange={value => { setDraft(value); setNotice(''); }}
    onCaptchaChange={value => { setCaptchaDraft(value); setNotice(''); }} onSave={save}
    onReload={() => { if (!pending.current) { focusFeedback.current = true; load('Latest settings loaded. Review before saving.'); } }} />;
}

export default function PlatformRegistrationSettings({ platform }) {
  return canManageRegistrationSettings(platform) ? <RegistrationSettingsController /> : null;
}
